import React from 'react';
import { Box, Paper, Typography, Stack, Tooltip, CircularProgress } from '@mui/material';
import { InfoOutlined, WarningAmberOutlined } from '@mui/icons-material';
import { P, cardSx, labelSx, PX } from './workspaceKit';
import { TaxCalculation, taxTreatmentLabel } from '../../services/taxService';

/**
 * The shared tax breakup panel.
 *
 * Every transaction workspace renders THIS rather than its own totals block,
 * so a purchase order, a sales invoice and a subcontracting bill all present
 * tax the same way — and, more importantly, all present the SERVER's tax
 * rather than a number the page worked out for itself.
 *
 * It shows not just the split but WHY it came out that way: which of the
 * company's GSTINs was used, what the place of supply is, and the engine's
 * plain-English notes. A finance user seeing "IGST" on a local customer needs
 * to be able to find out why without opening a support ticket.
 */

const inr = (n: number | null | undefined) =>
  `₹${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

interface Row { label: string; value: number; hint?: string; }

export interface TaxSummaryPanelProps {
  tax: TaxCalculation | null;
  loading?: boolean;
  error?: string | null;
  /** Hide the explanation block on tight layouts. */
  compact?: boolean;
  title?: string;
}

export const TaxSummaryPanel: React.FC<TaxSummaryPanelProps> = ({
  tax, loading = false, error = null, compact = false, title = 'Tax & totals',
}) => {
  const rows: Row[] = [];
  if (tax) {
    rows.push({ label: 'Taxable value', value: tax.subtotal });
    if (tax.discountAmount > 0) rows.push({ label: 'Discount', value: -tax.discountAmount });
    if (tax.cgstAmount > 0) rows.push({ label: 'CGST', value: tax.cgstAmount });
    if (tax.sgstAmount > 0) rows.push({ label: 'SGST', value: tax.sgstAmount });
    if (tax.igstAmount > 0) rows.push({ label: 'IGST', value: tax.igstAmount });
    if (tax.cessAmount > 0) rows.push({ label: 'Compensation cess', value: tax.cessAmount });
    if (tax.roundOffAmount !== 0) rows.push({ label: 'Round off', value: tax.roundOffAmount });
  }

  return (
    <Paper elevation={0} sx={{ ...cardSx, overflow: 'hidden' }}>
      <Box sx={{ px: PX, py: 1.5, borderBottom: `1px solid ${P.line}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography sx={{ fontSize: '14px', fontWeight: 700, color: P.strong }}>{title}</Typography>
          {loading && <CircularProgress size={13} thickness={5} sx={{ color: P.muted }} />}
        </Stack>
        {tax && (
          <Stack direction="row" spacing={0.75} alignItems="center">
            <Box sx={{ px: 1.1, py: '3px', borderRadius: '7px', bgcolor: P.primarySoft, border: `1px solid ${P.primaryBorder}` }}>
              <Typography sx={{ fontSize: '11.5px', fontWeight: 700, color: P.primary }}>
                {taxTreatmentLabel(tax.taxTreatment)}
              </Typography>
            </Box>
            {tax.isReverseCharge && (
              <Box sx={{ px: 1.1, py: '3px', borderRadius: '7px', bgcolor: P.amberBg, border: '1px solid #fde68a' }}>
                <Typography sx={{ fontSize: '11.5px', fontWeight: 700, color: P.amber }}>Reverse charge</Typography>
              </Box>
            )}
          </Stack>
        )}
      </Box>

      {error && (
        <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ px: PX, py: 1.25, bgcolor: P.redBg }}>
          <WarningAmberOutlined sx={{ fontSize: 16, color: P.red, mt: '1px' }} />
          <Typography sx={{ fontSize: '12.5px', color: P.red }}>{error}</Typography>
        </Stack>
      )}

      {!tax && !loading && (
        <Box sx={{ px: PX, py: 2.5 }}>
          <Typography sx={{ fontSize: '13px', color: P.muted }}>
            Add a line with a quantity and a rate — the tax is worked out for you.
          </Typography>
        </Box>
      )}

      {tax && (
        <Box sx={{ px: PX, py: 1.75 }}>
          {rows.map((r) => (
            <Stack key={r.label} direction="row" justifyContent="space-between" sx={{ py: '5px' }}>
              <Typography sx={{ fontSize: '13px', color: P.text }}>{r.label}</Typography>
              <Typography sx={{ fontSize: '13.5px', fontWeight: 600, color: P.strong, fontVariantNumeric: 'tabular-nums' }}>
                {inr(r.value)}
              </Typography>
            </Stack>
          ))}

          <Stack
            direction="row" justifyContent="space-between" alignItems="center"
            sx={{ mt: 1, pt: 1.25, borderTop: `1px solid ${P.line}` }}
          >
            <Typography sx={{ fontSize: '13.5px', fontWeight: 700, color: P.strong }}>Grand total</Typography>
            <Typography sx={{ fontSize: '19px', fontWeight: 800, color: P.strong, fontVariantNumeric: 'tabular-nums' }}>
              {inr(tax.totalAmount)}
            </Typography>
          </Stack>

          {/* Under reverse charge the supplier is paid the taxable value only —
              showing only the grand total would overstate what goes out. */}
          {tax.isReverseCharge && (
            <Stack direction="row" justifyContent="space-between" sx={{ mt: '6px' }}>
              <Typography sx={{ fontSize: '12.5px', color: P.amber, fontWeight: 600 }}>
                Payable to the supplier
              </Typography>
              <Typography sx={{ fontSize: '13.5px', fontWeight: 700, color: P.amber, fontVariantNumeric: 'tabular-nums' }}>
                {inr(tax.payableToParty)}
              </Typography>
            </Stack>
          )}

          {!compact && (
            <Box sx={{ mt: 1.75, pt: 1.5, borderTop: `1px dashed ${P.line}` }}>
              <Typography sx={{ ...labelSx, mb: '8px' }}>How this was determined</Typography>
              <Stack spacing={0.6}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography sx={{ fontSize: '12px', color: P.muted }}>Our GSTIN</Typography>
                  <Typography sx={{ fontSize: '12px', fontWeight: 600, color: tax.company.registered ? P.text : P.red, fontFamily: 'monospace' }}>
                    {tax.company.gstin || 'not configured'}
                  </Typography>
                </Stack>
                {tax.party && (
                  <Stack direction="row" justifyContent="space-between">
                    <Typography sx={{ fontSize: '12px', color: P.muted }}>
                      {tax.party.kind === 'CUSTOMER' ? 'Customer GSTIN' : 'Vendor GSTIN'}
                    </Typography>
                    <Typography sx={{ fontSize: '12px', fontWeight: 600, color: P.text, fontFamily: 'monospace' }}>
                      {tax.party.gstin || 'unregistered'}
                    </Typography>
                  </Stack>
                )}
                <Stack direction="row" justifyContent="space-between">
                  <Typography sx={{ fontSize: '12px', color: P.muted }}>Place of supply</Typography>
                  <Typography sx={{ fontSize: '12px', fontWeight: 600, color: P.text }}>
                    {tax.placeOfSupply || '—'}
                  </Typography>
                </Stack>
              </Stack>

              {tax.explanation?.length > 0 && (
                <Stack spacing={0.5} sx={{ mt: 1.25 }}>
                  {tax.explanation.map((note, i) => (
                    <Stack key={i} direction="row" spacing={0.75} alignItems="flex-start">
                      <InfoOutlined sx={{ fontSize: 13, color: P.muted, mt: '2px', flexShrink: 0 }} />
                      <Typography sx={{ fontSize: '11.5px', color: P.muted, lineHeight: 1.45 }}>{note}</Typography>
                    </Stack>
                  ))}
                </Stack>
              )}
            </Box>
          )}
        </Box>
      )}
    </Paper>
  );
};

/**
 * The per-line tax cells, for the line-item grid. Read-only by design: the
 * rate is a property of the product's tax category, not something to retype
 * on every line of every document.
 */
export const TaxRateCell: React.FC<{
  rate?: number | null;
  source?: string | null;
  overridden?: boolean;
}> = ({ rate, source, overridden }) => (
  <Tooltip
    title={
      rate == null
        ? 'Pick a product — its tax category sets the rate'
        : overridden
          ? 'Manually overridden for this line'
          : `From ${({
            'product.taxCategory': 'the product’s tax category',
            'hsn.taxCategory': 'the HSN code’s tax category',
            'product.gstRate': 'the product master',
            'hsn.gstRate': 'the HSN master',
            'line.taxCategoryId': 'the tax category chosen on this line',
            tenantDefault: 'your default tax category',
          } as Record<string, string>)[source || ''] || 'the tax masters'}`
    }
    arrow
  >
    <Typography
      component="span"
      sx={{
        fontSize: '13.5px', fontWeight: 600, fontVariantNumeric: 'tabular-nums',
        color: overridden ? P.amber : P.text,
        borderBottom: `1px dotted ${overridden ? P.amber : P.border}`,
        cursor: 'help',
      }}
    >
      {rate == null ? '—' : `${Number(rate)}%`}
    </Typography>
  </Tooltip>
);

export default TaxSummaryPanel;
